/**
 * StatCan (Canada) adapter (M9-B) — Statistics Canada Census of Population, over
 * the standard census geographies (province / census division / census
 * subdivision / dissemination area). Proves the adapter abstraction: it exposes
 * the SAME {@link OverlayAdapter} contract as US Census, so its variables render
 * through the M9-A engine with ZERO source-specific engine/render code.
 *
 * Boundaries: StatCan Boundary Files (served as GeoJSON at ETL time), keyed by
 * DGUID. Values: the StatCan Census profile (CSV: `dguid,value` rows). Fetch is
 * behind the {@link HttpClient} seam; normalize is pure. Per-country labels/units
 * — no forced cross-country normalization (M9.md).
 */

import type { Boundary, DemographicDatum, DemographicVariable, Tier } from "../types";
import { buildCatalog, normalizeFeatureCollection, type FeatureCollection, type Seed } from "./shared";
import type { HttpClient, OverlayAdapter } from "./types";

export const STATCAN_SOURCE = "statcan" as const;
export const STATCAN_TIERS = ["province", "cd", "csd", "da"] as const;

export const STATCAN_CITATION =
  "Statistics Canada, Census of Population. Reproduced and distributed on an \"as is\" basis with the permission of Statistics Canada.";

export const STATCAN_API_BASE = "https://www12.statcan.gc.ca/rest/census";

const STATCAN_SEEDS: Seed[] = [
  ["POP_2021", "Population, 2021", "people"],
  ["POP_DENSITY", "Population density per square kilometre", "people/km²"],
  ["MEDIAN_AGE", "Median age of the population", "years"],
  ["TOTAL_DWELLINGS", "Total private dwellings", "dwellings"],
  ["OCCUPIED_DWELLINGS", "Private dwellings occupied by usual residents", "dwellings"],
  ["MEDIAN_HH_INCOME", "Median total income of households (2020)", "CAD"],
  ["MEDIAN_INDIV_INCOME", "Median total income of individuals (2020)", "CAD"],
  ["AVG_HH_SIZE", "Average household size", "people"],
  ["IMMIGRANT_POP", "Immigrant population", "people"],
  ["BILINGUAL_POP", "Population with knowledge of English and French", "people"],
];

/** The static StatCan variable catalog — no network. */
export const STATCAN_CATALOG: DemographicVariable[] = buildCatalog(STATCAN_SEEDS, {
  country: "CA",
  citation: STATCAN_CITATION,
  tiers: STATCAN_TIERS,
});

export function createStatCanAdapter(): OverlayAdapter {
  return {
    source: STATCAN_SOURCE,
    country: "CA",
    tiers: STATCAN_TIERS,

    catalog() {
      return STATCAN_CATALOG;
    },

    async fetchRawBoundaries(http: HttpClient, tier: Tier, vintage) {
      return http.getJson<FeatureCollection>(`${STATCAN_API_BASE}/${vintage}/boundaries/${tier}.geojson`);
    },

    normalizeBoundaries(raw, tier, vintage): Boundary[] {
      return normalizeFeatureCollection(raw, {
        country: "CA",
        tier,
        vintage,
        idFrom: (props) => String(props.DGUID ?? props.dguid ?? props.GEOUID ?? ""),
        nameFrom: (props) => props.CSDNAME ?? props.PRNAME ?? props.NAME ?? props.name,
      });
    },

    async fetchRawValues(http: HttpClient, variableId: string, tier: Tier, vintage) {
      return http.getText(`${STATCAN_API_BASE}/${vintage}/profile/${tier}/${variableId}.csv`);
    },

    normalizeValues(raw, variableId, _tier, vintage): DemographicDatum[] {
      const text = String(raw ?? "");
      const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
      if (lines.length < 2) return [];
      // Header: `dguid,value`. Rows: DGUID + numeric value.
      const out: DemographicDatum[] = [];
      for (let i = 1; i < lines.length; i++) {
        const [id, valStr] = lines[i]!.split(",");
        if (!id) continue;
        const num = valStr == null || valStr.trim() === "" ? null : Number(valStr);
        out.push({
          boundaryId: id.trim(),
          variableId,
          vintage,
          value: num !== null && Number.isFinite(num) ? num : null,
        });
      }
      return out;
    },
  };
}
